import Button, { ActionButton } from '@bugbug/core/components/Button';
import Checkbox from '@bugbug/core/components/Checkbox';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CancelSubscriptionFeedbackDto } from './CancelSubscriptionModal.schemas';

import type { CustomEventHandler } from '@bugbug/core/types/forms';
import TextArea from '~/components/TextArea';
import useActionState from '~/hooks/useActionState';
import useModal from '~/hooks/useModal';
import { OrganizationActions } from '~/modules/organization/organization.redux';
import {
  selectSubscription,
  selectCurrentOrganizationId,
} from '~/modules/organization/organization.selectors';
import { useAppDispatch, useAppSelector } from '~/modules/store';
import { subscriptionsApi } from '~/modules/subscriptions/subscriptions.api';
import analytics, { TRACK_EVENT_ARG_TYPE } from '~/services/analytics';

import { DowngradeConfirmedModal } from '../DowngradeConfirmedModal/DowngradeConfirmedModal';
import {
  SubscriptionModalContainer,
  SubscriptionModalFooter,
  SubscriptionModalTitle,
  SubscriptionModalDetails,
  SubscriptionModalContent,
  ErrorMessage,
} from '../Subscription/Subscription.styled';

import { CANCEL_REASONS, CANCEL_REASON_LABEL } from './CancelSubscriptionModal.constants';
import { cancelSubscriptionFeedbackSchema } from './CancelSubscriptionModal.schemas';
import * as S from './CancelSubscriptionModal.styled';

import QuestionMarkIcon from '~/images/question-mark-icon.svg?react';

export const CancelSubscriptionModal = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'cancelSubscriptionModal' });
  const modal = useModal();
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector(selectCurrentOrganizationId);
  const activeSubscription = useAppSelector(selectSubscription);

  const [step, setStep] = useState<'cancelReasons' | 'otherFeedback'>('cancelReasons');
  const [serverError, setServerError] = useState<string | null>(null);

  const onSubmit = (values: CancelSubscriptionFeedbackDto) => {
    dispatch(
      OrganizationActions.subscribeRequest({
        price: 'FREE',
        addons: [],
        paymentMethodId: null,
        coupon: null,
        feedback: values,
      }),
    );
  };

  const onConfirmReasons = async () => {
    const errors = await formik.validateForm();
    if (!errors.reasons) {
      setStep('otherFeedback');
    }
  };

  const formik = useFormik<CancelSubscriptionFeedbackDto>({
    initialValues: {
      other: '',
      reasons: [],
    },
    validationSchema: cancelSubscriptionFeedbackSchema,
    onSubmit,
  });

  const selectedReasons = formik.values.reasons ?? [];

  const onReasonChange = (event: CustomEventHandler) => {
    const { name } = event.target;

    if (selectedReasons.includes(name)) {
      formik.setFieldValue(
        'reasons',
        selectedReasons.filter((reason) => reason !== name),
      );
    } else {
      formik.setFieldValue('reasons', [...selectedReasons, name]);
    }
  };

  const handleSubmitSuccess = () => {
    dispatch(subscriptionsApi.util.invalidateTags(['subscriptions']));
    modal.showCustom(DowngradeConfirmedModal);
  };

  const handleSubmitError = (requestErrors: Record<string, string>) => {
    setServerError(
      Object.values(requestErrors)[0] ??
        t('unexpectedServerError', 'Unexpected server error. Try again.'),
    );
  };

  const cancelSubscriptionRequestState = useActionState(OrganizationActions.subscribeRequest, {
    onFailure: handleSubmitError,
    onSuccess: handleSubmitSuccess,
  });

  const cancelDowngrade = () => {
    analytics.trackEvent('cancel_downgrade_button_clicked', {
      [TRACK_EVENT_ARG_TYPE.BUTTON_TYPE]: 'changed_my_mind',
      [TRACK_EVENT_ARG_TYPE.ORGANIZATION_ID]: organizationId,
      [TRACK_EVENT_ARG_TYPE.ACCOUNT_PLAN]: activeSubscription?.type ?? 'free',
      [TRACK_EVENT_ARG_TYPE.PLAN_DOWNGRADED_TO]: 'free',
      [TRACK_EVENT_ARG_TYPE.DOWNGRADE_TYPE]: 'plan_downgrade',
    });
    modal.hide();
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <SubscriptionModalContainer>
        <SubscriptionModalContent>
          <QuestionMarkIcon width={48} height={48} />
          <SubscriptionModalDetails>
            {step === 'cancelReasons' && (
              <>
                <SubscriptionModalTitle>
                  {t('cancelReasonsTitle', 'Why, oh why?')}
                </SubscriptionModalTitle>
                <div>
                  <S.Description>
                    {t('question', 'Could you tell us what made you change your plan?')}
                  </S.Description>
                  <S.Description>{t('feedback', 'All feedback is important to us!')}</S.Description>
                </div>
                {CANCEL_REASONS.map((reason) => (
                  <Checkbox
                    key={reason}
                    name={reason}
                    checked={selectedReasons.includes(reason)}
                    onChange={onReasonChange}
                    aria-label={CANCEL_REASON_LABEL[reason]}
                  >
                    {CANCEL_REASON_LABEL[reason]}
                  </Checkbox>
                ))}
              </>
            )}
            {step === 'otherFeedback' && (
              <>
                <SubscriptionModalTitle>
                  {t('otherFeedbackTitle', 'Are you sure you want to downgrade?')}
                </SubscriptionModalTitle>
                <S.Description>
                  {t(
                    'otherFeedback',
                    'Tell us how we can make BugBug better. We read every answer! ',
                  )}
                </S.Description>
                <TextArea
                  {...formik.getFieldProps('other')}
                  {...formik.getFieldMeta('other')}
                  autoSize
                  maxInitialHeight={220}
                  onChange={formik.handleChange}
                  initialHeight={87}
                  placeholder={t('feedbackPlaceholder', 'Add your feedback...')}
                />
              </>
            )}

            {serverError && <ErrorMessage>{serverError}</ErrorMessage>}
            {formik.errors.reasons && <ErrorMessage>{formik.errors.reasons}</ErrorMessage>}
          </SubscriptionModalDetails>
        </SubscriptionModalContent>
        <SubscriptionModalFooter>
          <Button
            variant="primary"
            bordered
            onClick={cancelDowngrade}
            disabled={cancelSubscriptionRequestState.isLoading}
          >
            {t('cancel', 'I changed my mind, keep my plan')}
          </Button>
          {step === 'cancelReasons' && (
            <Button bordered variant="danger" onClick={onConfirmReasons}>
              {t('continueDowngrade', 'Continue downgrade')}
            </Button>
          )}
          {step === 'otherFeedback' && (
            <ActionButton
              bordered
              type="submit"
              variant="danger"
              onClick={onConfirmReasons}
              disabled={cancelSubscriptionRequestState.isLoading || !!formik.errors.other}
              pending={cancelSubscriptionRequestState.isLoading}
            >
              {t('confirmDowngrade', 'Confirm downgrade')}
            </ActionButton>
          )}
        </SubscriptionModalFooter>
      </SubscriptionModalContainer>
    </form>
  );
};
